import { useEffect, useState, type ReactNode, type RefObject } from 'react'

import { cn } from '@/lib/cn'

import { Reveal } from './motion'

/** Largeur de lecture unique — le rythme de la page vient de là. */
export function Container({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('mx-auto w-full max-w-6xl px-6', className)}>{children}</div>
}

export function Section({
  id,
  className,
  children,
}: {
  id?: string
  className?: string
  children?: ReactNode
}) {
  return (
    // Le rythme vient d'un token unique : resserrer la page se fait à un
    // seul endroit, et toutes les sections suivent.
    <section
      id={id}
      className={cn('scroll-mt-24 py-[var(--space-section)]', 'sm:py-[var(--space-section-lg)]', className)}
    >
      {children ? (
        <Container>
          <Reveal>{children}</Reveal>
        </Container>
      ) : null}
    </section>
  )
}

/** Surtitre en petites capitales espacées : la seule dorure autorisée. */
export function Overline({ className, children }: { className?: string; children: ReactNode }) {
  return <p className={cn('overline text-champagne-400', className)}>{children}</p>
}

export function SectionTitle({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <h2 className={cn('mt-3 font-display text-heading-lg text-content-primary', className)}>
      {children}
    </h2>
  )
}

export function Card({
  className,
  children,
  interactive = false,
}: {
  className?: string
  children: ReactNode
  interactive?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-lg border border-line-subtle bg-bg-elevated p-6',
        interactive && 'lift hover:border-line-strong',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'ember' | 'champagne' | 'success'
  children: ReactNode
}) {
  const tones = {
    neutral: 'border-line text-content-secondary',
    ember: 'border-ember-400/40 text-ember-300',
    champagne: 'border-champagne-400/40 text-champagne-300',
    success: 'border-state-success/40 text-state-success',
  } as const

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-3 py-1 text-caption uppercase',
        tones[tone],
      )}
    >
      {children}
    </span>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-bg-overlay', className)} />
}

export function Divider({ className }: { className?: string }) {
  return <hr className={cn('border-0 border-t border-line-subtle', className)} />
}

/**
 * La place d'une barre collée en bas de l'écran (panier, total).
 *
 * Une marge fixe sous la page (`pb-40`, `pb-72`) ne suit pas la barre : elle
 * grandit avec le panier, et sur un téléphone le dernier article et le champ
 * de note finissaient dessous, impossibles à atteindre. Ce bloc vide mesure la
 * barre et en prend exactement la hauteur, encoche comprise.
 */
export function BottomBarSpacer({
  barRef,
  className,
}: {
  barRef: RefObject<HTMLElement | null>
  className?: string
}) {
  const [height, setHeight] = useState(0)

  // Rebranché à CHAQUE rendu, pas une fois : la barre peut apparaître après
  // ce bloc (onglets calculés une fois les droits chargés) ou être remplacée.
  // Mesurée une seule fois au montage, elle restait à 0 dans ce cas — et le
  // dernier bouton de la page passait dessous, selon l'ordre de chargement.
  // Un effet « passif », pas `useLayoutEffect` : quand la barre suit ce bloc
  // dans la page (l'accueil), sa référence n'est pas encore posée au moment
  // des effets de mise en page de ce bloc ; elle l'est toujours ici.
  // Chaque passage débranche lui-même son observateur : aucun état partagé
  // qu'un démontage-remontage (StrictMode) laisserait débranché.
  useEffect(() => {
    const bar = barRef.current
    if (!bar) {
      setHeight(0)
      return
    }
    const measure = () => setHeight(bar.offsetHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(bar)
    return () => observer.disconnect()
  })

  return <div aria-hidden className={className} style={{ height }} />
}
